import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Phone, FileText, LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce, useClickOutside } from '@/hooks';
import { omniSearch, type OmniSearchDIDResult, type OmniSearchPortOrderResult } from '@/features/dids/api';

interface OmniSearchProps {
  className?: string;
}

export function OmniSearch({ className }: OmniSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<{
    dids: OmniSearchDIDResult[];
    port_orders: OmniSearchPortOrderResult[];
  }>({ dids: [], port_orders: [] });

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => setIsOpen(false));

  // Global keyboard shortcut: Cmd+K (Mac) / Ctrl+K (Windows)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      // Close on Escape
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Fetch search results when debounced query changes
  useEffect(() => {
    const fetchResults = async () => {
      if (debouncedQuery.length < 3) {
        setResults({ dids: [], port_orders: [] });
        return;
      }

      setIsLoading(true);
      try {
        const data = await omniSearch(debouncedQuery);
        setResults(data);
      } catch (error) {
        console.error('Search failed:', error);
        setResults({ dids: [], port_orders: [] });
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [debouncedQuery]);

  // Handle DID click - navigate to Phone Numbers page with drawer open
  const handleDIDClick = useCallback((did: OmniSearchDIDResult) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/dids?did=${did.tn}&drawer=config`);
  }, [navigate]);

  // Handle Port Order click - navigate to Porting page with drawer open
  const handlePortOrderClick = useCallback((port: OmniSearchPortOrderResult) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/porting?order=${port.id}&drawer=timeline`);
  }, [navigate]);

  // Format port order status for display
  const formatStatus = (status: string | null) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const hasResults = results.dids.length > 0 || results.port_orders.length > 0;
  const showDropdown = isOpen && (hasResults || isLoading || (query.length >= 3 && !isLoading));

  // Detect OS for keyboard shortcut hint
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutHint = isMac ? '\u2318K' : 'Ctrl+K';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Search Input */}
      <div className="relative">
        <Search
          size={18}
          strokeWidth={1.5}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search DIDs, orders..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value.length > 0) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            if (query.length > 0) {
              setIsOpen(true);
            }
          }}
          className={cn(
            'w-64 pl-10 pr-12 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg',
            'placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
            'transition-all'
          )}
        />
        {/* Keyboard shortcut hint */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center">
          <kbd className="px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-200 rounded border border-slate-300">
            {shortcutHint}
          </kbd>
        </div>
      </div>

      {/* Results Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50 animate-fade-in">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : !hasResults && query.length >= 3 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              No results found for "{query}"
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {/* DIDs Section */}
              {results.dids.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                    DIDs
                  </div>
                  {results.dids.map((did) => (
                    <button
                      key={did.tn}
                      onClick={() => handleDIDClick(did)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Phone size={16} className="text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono font-medium text-slate-900 truncate">
                          {did.tn_formatted}
                        </p>
                        {(did.rate_center || did.state) && (
                          <p className="text-xs text-slate-500 truncate">
                            {[did.rate_center, did.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Port Orders Section */}
              {results.port_orders.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-t border-slate-100">
                    Port Orders
                  </div>
                  {results.port_orders.map((port) => (
                    <button
                      key={port.id}
                      onClick={() => handlePortOrderClick(port)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <FileText size={16} className="text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono font-medium text-slate-900">
                          #PORT-{port.id}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {port.porting_from || 'Unknown carrier'} &bull; {formatStatus(port.status)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
